import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, of, Subject } from 'rxjs';
import {
  faFileSignature,
  faEnvelope,
  faEye,
  faEyeSlash,
  faRotate,
  faExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { ReportControllerControllerV2 } from '../ui-report-controller-v2.service';
import { I18NextService } from 'angular-i18next';
import { Report, ReportType } from '@fullyops/legacy/data/api/types/Report';
import { DialogDocumentViewComponent } from '@fullyops/legacy/ui/ui-shared/dialog-document-view/dialog.component';
import { UiCompaniesService } from '../ui-companies.service';
import { UiContactsService } from '../ui-contacts.service';
import { map, switchMap, takeUntil, timeout } from 'rxjs/operators';
import { SupportTicketResponse } from '@fullyops/legacy/data/api/types/SupportTicket';
import { TimeZoneDateFormatterService } from '@fullyops/shared/services/date-formatter.service';
import {
  EmailRecipients,
  SendEmailDialogComponent,
  SendEmailDialogData,
} from '@fullyops/shared/components/send-email-dialog/send-email-dialog.component';
import { ApiTenantConfigurationService } from '@fullyops/data/tenant-configuration/api-adapter';
import { SidenavPortalBridgeService } from '@fullyops/core/sidenav-form-portal-bridge.service';
import {
  REPORT_SIGNATURE_FORM_TOKEN,
  ReportSignatureFormComponent,
} from '@fullyops/features/work-orders/containers/report-signature-form/report-signature-form.component';

@Component({
  selector: 'crm-report-list',
  templateUrl: './report-list.component.html',
  styleUrls: ['./report-list.component.scss'],
  standalone: false,
})
export class ReportListComponent implements OnInit, OnDestroy {
  @Input() ticketId: string;
  @Input() isDetailPage: boolean;
  @Input() reportType: ReportType;
  @Input() withProposal = false;
  @Input() reports$: BehaviorSubject<Report[]>;
  @Input() ticket$: BehaviorSubject<SupportTicketResponse>;

  actionLabel: string;

  faFileSignature = faFileSignature;
  faEnvelope = faEnvelope;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  faRotate = faRotate;
  feExclamation = faExclamation;

  destroy$ = new Subject();

  constructor(
    public dialog: MatDialog,
    protected reportControllerControllerV2: ReportControllerControllerV2,
    protected companiesService: UiCompaniesService,
    protected contactsService: UiContactsService,
    public timezoneDate: TimeZoneDateFormatterService,
    private tenantConfigController: ApiTenantConfigurationService,
    protected i18next: I18NextService,
    private sidebarService: SidenavPortalBridgeService,
  ) {}

  ngOnInit() {
    this.actionLabel = this.withProposal ? 'generateReport' : 'signReport';
  }

  getReportsSorted() {
    return this.reports$.value.sort((reportA, reportB) => {
      const reportACreatedAt = this.timezoneDate.formatInTimezone(
        reportA.createdAt,
      );
      const reportBCreatedAt = this.timezoneDate.formatInTimezone(
        reportB.createdAt,
      );
      return reportBCreatedAt.valueOf() - reportACreatedAt.valueOf();
    });
  }

  downloadReport({
    reportId,
    internal,
  }: {
    reportId: string;
    internal?: boolean;
  }) {
    this.reportControllerControllerV2
      .downloadReportAsURL({ reportId, internal })
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.dialog.open(DialogDocumentViewComponent, {
          maxWidth: '100vw',
          maxHeight: '100vh',
          height: '90%',
          width: '90%',
          data: {
            document: res,
            filename: `report-${reportId}-${
              internal ? 'internal' : 'external'
            }.pdf`,
          },
        });
      });
  }

  onOpen({ reportId }: { reportId: string }) {
    this.downloadReport({ reportId });
  }

  onOpenInternal({ reportId }: { reportId: string }) {
    this.downloadReport({ reportId, internal: true });
  }

  generateProposal() {
    this.reportControllerControllerV2
      .generatePreview({
        reportType: this.reportType,
        ticketId: this.ticketId,
        language: this.i18next.language,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((report) => {
        const newReports = [...this.reports$.value];
        newReports.unshift(report);
        this.reports$.next(newReports);
      });
  }

  openSendEmailDialog(reportId: string) {
    let to = this.ticket$.value.contacts.map((c) => c.email);
    to.push(this.ticket$.value.company.email);
    to = to.filter((addr) => addr != null);

    this.getDefaultBCC().subscribe((defaultBCC) => {
      this.dialog
        .open<SendEmailDialogComponent, SendEmailDialogData, EmailRecipients>(
          SendEmailDialogComponent,
          {
            data: {
              bcc: true,
              title: 'sendReportSelectRecipients',
              okLabel: 'sendReport',
              initialRecipients: { to, bcc: defaultBCC },
              suggestions: [...to, ...defaultBCC],
            },
          },
        )
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((addrs) => {
          if (addrs == null) {
            return;
          }
          this.reportControllerControllerV2
            .sendEmailReportWithCustomEmails({
              reportId,
              recipients: addrs.to,
              bccRecipients: addrs.bcc,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe();
        });
    });
  }

  private getDefaultBCC() {
    return this.tenantConfigController
      .getConfigurationsByLabel({
        label: 'SUPPORT_TICKET_REPORT_INTERNAL_EMAIL',
      })
      .pipe(
        // Fall back to TENANT_EMAIL if SUPPORT_TICKET_REPORT_INTERNAL_EMAIL isn't available.
        switchMap((res) => {
          if (res.value) {
            return of(res);
          }
          return this.tenantConfigController.getConfigurationsByLabel({
            label: 'TENANT_EMAIL',
          });
        }),
        map((res) => [res.value]),
        timeout({ each: 1000, with: () => of<string[]>([]) }),
        takeUntil(this.destroy$),
      );
  }

  signReport(ticketReport: Report, indexToUpdate: number) {
    this.sidebarService.openPortal(
      ReportSignatureFormComponent,
      REPORT_SIGNATURE_FORM_TOKEN,
      {
        onSign: (signature, comment) => {
          this.reportControllerControllerV2
            .handleSignatureUpload({
              signature,
              comment,
              reportType: ticketReport.type,
              reportId: ticketReport.id,
              language: this.i18next.language,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe((responseReport) => {
              this.sidebarService.closePortal();
              const newReports = [...this.reports$.value];
              newReports[indexToUpdate] = responseReport;
              this.reports$.next(newReports);
            });
        },
        onCancel: () => this.sidebarService.closePortal(),
      },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }
}
